import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw, Search, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

/** Table-like skeleton (loading state for lists). */
export const TableSkeleton = ({ rows = 5, className = "", testId = "table-skeleton" }) => (
  <div className={cn("space-y-2 p-4", className)} data-testid={testId} aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3">
        <Skeleton className="h-9 flex-1" />
        <Skeleton className="hidden h-9 w-24 sm:block" />
        <Skeleton className="hidden h-9 w-20 md:block" />
      </div>
    ))}
  </div>
);

/** Error state with retry (alert pattern from the design guidelines). */
export const ErrorState = ({ message, onRetry, className = "", testId = "error-state" }) => {
  const { t } = useTranslation();
  return (
    <Alert className={cn("border-rose-200 bg-rose-50 text-rose-900", className)} data-testid={testId}>
      <AlertTriangle className="h-4 w-4 !text-rose-900" aria-hidden="true" />
      <AlertTitle>{t("common.loadError")}</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center gap-3">
        <span>{message || t("common.error")}</span>
        {onRetry && (
          <Button size="sm" variant="outline" className="bg-card text-foreground" onClick={onRetry} data-testid={`${testId}-retry`}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />{t("common.retry")}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
};

/** Debounced search field. `onChange` receives the debounced value. */
export const SearchInput = ({ value, onChange, placeholder, className = "", testId = "search-input", delay = 300 }) => {
  const { t } = useTranslation();
  const [text, setText] = useState(value || "");
  useEffect(() => { setText(value || ""); }, [value]);
  useEffect(() => {
    if (text === (value || "")) return undefined;
    const id = setTimeout(() => onChange(text), delay);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return (
    <div className={cn("relative", className)}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder || t("common.searchPlaceholder")} className="bg-card pl-8 pr-8" data-testid={testId} aria-label={placeholder || t("common.search")} />
      {text && (
        <button type="button" onClick={() => { setText(""); onChange(""); }} className="og-focus absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground" aria-label={t("common.remove")} data-testid={`${testId}-clear`}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

/** Card shell for list views: toolbar (filters/actions) + body + optional footer. */
export const ListCard = ({ toolbar, children, footer, refreshing = false, className = "", testId }) => (
  <section className={cn("og-card relative overflow-hidden", className)} data-testid={testId}>
    {toolbar && <div className="flex flex-col gap-3 border-b border-border px-4 py-3 md:flex-row md:flex-wrap md:items-center md:justify-between">{toolbar}</div>}
    <RefreshBar active={refreshing} />
    {children}
    {footer}
  </section>
);

/** Thin indeterminate bar shown while a list is re-fetching in the background (API latency feedback). */
export const RefreshBar = ({ active, className = "" }) => (
  <div className={cn("relative h-0.5 w-full overflow-hidden", className)} aria-hidden={!active} data-testid={active ? "refresh-bar" : undefined}>
    {active && <div className="absolute inset-y-0 w-1/4 rounded-full bg-primary/70" style={{ animation: "og-slide 1.2s ease-in-out infinite" }} />}
  </div>
);

/** Key/value grid used in detail panels. */
export const KeyValue = ({ items, className = "", testId }) => (
  <dl className={cn("grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2", className)} data-testid={testId}>
    {items.filter((i) => i && i.value !== undefined && i.value !== null && i.value !== "").map((i) => (
      <div key={i.label} className="min-w-0">
        <dt className="text-xs text-muted-foreground">{i.label}</dt>
        <dd className="break-words font-medium text-foreground" data-testid={i.testId}>{i.value}</dd>
      </div>
    ))}
  </dl>
);
